import ChevronLeft from '@assets/icons/chevron-left-thin.svg';
import ChevronRight from '@assets/icons/chevron-right-thin.svg';
import { Analytics } from '@/utils/analytics';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import { PaymentBanner, PaymentCarouselData } from './payment-banner';
import { shadow } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useRef, useState } from 'react';

interface Props {
  data: PaymentCarouselData[];
}

export const PaymentBannerCarousel: React.FC<Props> = ({ data }) => {
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  const listRef = useRef<FlatList>(null);
  const listContentWidthRef = useRef(0);

  const [carouselWidth, setCarouselWidth] = useState(0);
  const [offsetX, setOffsetX] = useState(0);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const currentOffset = event.nativeEvent.contentOffset.x;
    setOffsetX(currentOffset);
  };

  const renderArrows = Platform.OS === 'web' && data.length > 1;

  const bannerWidth =
    data.length > 1
      ? carouselWidth - 64
      : isSmallLayout
        ? carouselWidth - 32
        : carouselWidth - 48;

  return (
    <View
      onLayout={(e) => {
        setCarouselWidth(e.nativeEvent.layout.width);
      }}
    >
      <FlatList<PaymentCarouselData>
        data={data}
        horizontal
        ItemSeparatorComponent={() => (
          <View
            style={
              isSmallLayout ? styles.separatorSmallLayout : styles.separator
            }
          />
        )}
        ListFooterComponent={() => (
          <View
            style={
              isSmallLayout ? styles.headerFooterSmallLayout : styles.footer
            }
          />
        )}
        ListHeaderComponent={() => (
          <View
            style={
              isSmallLayout ? styles.headerFooterSmallLayout : styles.header
            }
          />
        )}
        onContentSizeChange={(width) => {
          listContentWidthRef.current = width;
        }}
        onScroll={handleScroll}
        ref={listRef}
        renderItem={({ item }) => (
          <View style={styles.bannerContainer}>
            <PaymentBanner
              {...item}
              customStyles={[
                isSmallLayout ? styles.bannerSmallLayout : styles.banner,
                { width: bannerWidth },
              ]}
            />
          </View>
        )}
        showsHorizontalScrollIndicator={false}
      />
      {renderArrows ? (
        <View style={styles.arrowWrapper}>
          <TouchableOpacity
            accessibilityLabel="auto-payment-carousel-left-arrow"
            onPress={() => {
              listRef.current?.scrollToOffset({
                animated: true,
                offset: 0,
              });
              Analytics.track('next_payment_carousel.left_arrow.press');
            }}
          >
            <ChevronLeft
              height={16}
              stroke={
                offsetX === 0
                  ? theme.sk.color.icon.disabled
                  : theme.sk.color.icon.primary
              }
              width={16}
            />
          </TouchableOpacity>
          <TouchableOpacity
            accessibilityLabel="auto-payment-carousel-right-arrow"
            onPress={() => {
              listRef.current?.scrollToOffset({
                animated: true,
                offset: listContentWidthRef.current,
              });
              Analytics.track('next_payment_carousel.right_arrow.press');
            }}
          >
            <ChevronRight
              height={16}
              stroke={
                offsetX > 0
                  ? theme.sk.color.icon.disabled
                  : theme.sk.color.icon.primary
              }
              width={16}
            />
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  arrowWrapper: {
    flexDirection: 'row',
    gap: 24,
    justifyContent: 'center',
    marginVertical: 16,
  },
  banner: {
    ...shadow({
      elevation: 5,
      opacity: 0.15,
      radius: Platform.OS === 'web' ? 8 : 2,
      x: 0,
      y: 2,
    }),
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.colors.transparent,
    borderRadius: 8,
    borderWidth: 1,
    marginVertical: 8,
    padding: 12,
  },
  bannerContainer: {
    justifyContent: 'center',
  },
  bannerSmallLayout: {
    ...shadow({
      elevation: 5,
      opacity: 0.15,
      radius: 2,
      x: 0,
      y: 2,
    }),
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: 8,
    marginVertical: 8,
    padding: 12,
    width: 320,
  },
  footer: {
    marginHorizontal: 0,
    width: 24,
  },
  header: {
    marginHorizontal: 0,
    width: 24,
  },
  headerFooterSmallLayout: {
    marginHorizontal: 0,
    width: 16,
  },
  separator: {
    marginHorizontal: 0,
    width: 16,
  },
  separatorSmallLayout: {
    marginHorizontal: 0,
    width: 8,
  },
}));
